import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { SxProps } from "@mui/system";
import {
  Controller,
  FieldValues,
  RegisterOptions,
  useFormContext,
  ValidateResult,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import DateField from "../../dateField/DateField";

type Props = {
  dateName: string;
  indefiniteFieldName: string;
  indefiniteLabel?: string;
  dateRequired?: boolean;
  disabled?: boolean;
  sx?: SxProps;
  validate?: (value: Date, fieldValues?: FieldValues) => ValidateResult;
  indefiniteRules?: RegisterOptions;
  error?: boolean;
};

/**
 * Date field with "indefinite/until further notice" checkbox below it.
 *
 * When the checkbox is checked:
 * - Date field is disabled
 * - Date value is set to null
 */
const DateWithIndefiniteField = ({
  dateName,
  indefiniteFieldName,
  indefiniteLabel,
  dateRequired = false,
  disabled = false,
  sx = {},
  validate,
  indefiniteRules,
  error = false,
}: Props) => {
  const { t } = useTranslation("talentProfile");

  const { watch, setValue, control, clearErrors } = useFormContext();

  const indefinite = watch(indefiniteFieldName);

  return (
    <Box>
      <Box sx={sx}>
        <DateField
          name={dateName}
          disabled={disabled || indefinite}
          required={
            dateRequired
              ? { value: true, message: "required" }
              : { value: false, message: "" }
          }
          validate={validate}
          error={error}
        />
      </Box>
      <Box sx={{ width: "100%", mt: 1 }}>
        <Controller
          name={indefiniteFieldName}
          control={control}
          rules={indefiniteRules}
          render={({ field: { value, onChange: fieldOnChange } }) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={value || false}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    fieldOnChange(checked);
                    if (checked) {
                      setValue(dateName, null);
                      clearErrors(dateName);
                    }
                  }}
                  disabled={disabled}
                />
              }
              label={indefiniteLabel || t("validUFN")}
            />
          )}
        />
      </Box>
    </Box>
  );
};

export default DateWithIndefiniteField;
